import { getBuffer } from '@dynamic-labs-sdk/client/core';
import bs58 from 'bs58';
import nacl from 'tweetnacl';

import { PhantomRedirectDecryptionError } from '../../../errors/PhantomRedirectDecryptionError';

export type DecryptPayloadParams = {
  data: string;
  nonce: string;
  sharedSecret: string;
};

/**
 * Decrypts a payload using NaCl box encryption with a shared secret.
 *
 * @param params.data - The base58-encoded encrypted data
 * @param params.nonce - The base58-encoded nonce
 * @param params.sharedSecret - The base58-encoded shared secret
 * @returns The decrypted payload object
 * @throws {PhantomRedirectDecryptionError} If decryption fails
 */
export const decryptPayload = ({
  data,
  nonce,
  sharedSecret,
}: DecryptPayloadParams): Record<string, unknown> => {
  let decrypted: Uint8Array | null;

  try {
    decrypted = nacl.box.open.after(
      bs58.decode(data),
      bs58.decode(nonce),
      bs58.decode(sharedSecret)
    );
  } catch {
    throw new PhantomRedirectDecryptionError();
  }

  if (!decrypted) {
    throw new PhantomRedirectDecryptionError();
  }

  const decryptedString = getBuffer().from(decrypted).toString('utf-8');

  try {
    return JSON.parse(decryptedString) as Record<string, unknown>;
  } catch {
    throw new PhantomRedirectDecryptionError();
  }
};
